import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, EyeIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, EnvelopeIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { LeadForm } from '../../components/forms/LeadForm'
import { LeadStatusBadge, LeadRatingBadge } from '../../components/ui/StatusBadge'
import { Avatar } from '../../components/ui/Avatar'
import { Pagination } from '../../components/ui/Pagination'
import { useTranslation } from '../../hooks/useTranslation'
import { useUserLookup } from '../../hooks/useUsers'
import type { Lead, LeadInput } from '../../../shared/types'

const leadsApi = createCrudApi<Lead, LeadInput>('leads')

const STATUSES = ['new', 'contacted', 'qualified', 'unqualified'] as const
type Status = typeof STATUSES[number]
type SortField = 'name' | 'company' | 'email' | 'status' | 'rating' | 'tier' | 'b2bScore' | 'category'
type SortDirection = 'asc' | 'desc'

const STATUS_ORDER: Record<Status, number> = {
  new: 0,
  contacted: 1,
  qualified: 2,
  unqualified: 3,
}

const RATING_ORDER: Record<string, number> = {
  hot: 0,
  warm: 1,
  cold: 2,
}

export function LeadsListPage() {
  const [showForm, setShowForm] = useState(false)
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all')
  const [tierFilter, setTierFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [salesPlayFilter, setSalesPlayFilter] = useState<string>('all')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { getUser } = useUserLookup()

  // Build filters object for API
  const filters: Record<string, string> = {}
  if (statusFilter !== 'all') filters.status = statusFilter
  if (tierFilter !== 'all') filters.tier = tierFilter
  if (categoryFilter !== 'all') filters.category = categoryFilter
  if (salesPlayFilter !== 'all') filters.salesPlay = salesPlayFilter
  const hasFilters = Object.keys(filters).length > 0

  const { data: response, isLoading } = useQuery({
    queryKey: ['leads', { page, limit, filters }],
    queryFn: () => leadsApi.list({ page, limit, filters: hasFilters ? filters : undefined }),
  })

  const leads = response?.data

  // Handle filter change - reset to page 1
  const handleStatusFilter = (status: Status | 'all') => {
    setStatusFilter(status)
    setPage(1)
  }
  const handleTierFilter = (tier: string) => { setTierFilter(tier); setPage(1) }
  const handleCategoryFilter = (cat: string) => { setCategoryFilter(cat); setPage(1) }
  const handleSalesPlayFilter = (sp: string) => { setSalesPlayFilter(sp); setPage(1) }

  const createMutation = useMutation({
    mutationFn: leadsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setShowForm(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: leadsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Client-side sorting only (filtering is now server-side)
  const sortedLeads = useMemo(() => {
    if (!leads) return []

    const result = [...leads]

    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'name':
          const nameA = (a.lastName ? `${a.firstName || ''} ${a.lastName}` : a.company).toLowerCase()
          const nameB = (b.lastName ? `${b.firstName || ''} ${b.lastName}` : b.company).toLowerCase()
          comparison = nameA.localeCompare(nameB)
          break
        case 'company':
          comparison = (a.company || '').localeCompare(b.company || '')
          break
        case 'email':
          comparison = (a.email || '').localeCompare(b.email || '')
          break
        case 'status':
          comparison = STATUS_ORDER[a.status as Status] - STATUS_ORDER[b.status as Status]
          break
        case 'rating':
          comparison = (RATING_ORDER[a.rating || ''] ?? 99) - (RATING_ORDER[b.rating || ''] ?? 99)
          break
        case 'tier':
          comparison = (a.tier || '').localeCompare(b.tier || '')
          break
        case 'b2bScore':
          comparison = (a.b2bScore ?? -1) - (b.b2bScore ?? -1)
          break
        case 'category':
          comparison = (a.category || '').localeCompare(b.category || '')
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [leads, sortField, sortDirection])

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  if (isLoading) return <div>{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.leads.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.leads.description')}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {showForm ? t('common.cancel') : t('pages.leads.addButton')}
          </button>

          <Link
            to="/leads/mailing-export"
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 ring-1 ring-gray-300"
          >
            <EnvelopeIcon className="h-5 w-5" />
            Mailing Export
          </Link>

          {showForm && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.leads.newTitle')}</h3>
              <LeadForm
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setShowForm(false)}
                loading={createMutation.isPending}
              />
            </div>
          )}

          {/* Status Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')} {t('fields.status')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleStatusFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {STATUSES.map(status => {
                const statusLabels: Record<Status, string> = {
                  new: t('status.new'),
                  contacted: t('status.contacted'),
                  qualified: t('status.qualified'),
                  unqualified: t('status.unqualified'),
                }
                return (
                  <button
                    key={status}
                    onClick={() => handleStatusFilter(status)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      statusFilter === status
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {statusLabels[status]}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Tier Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')} Tier</h3>
            <div className="flex flex-wrap gap-2">
              {['all', 'Tier 1', 'Tier 2'].map(tier => (
                <button
                  key={tier}
                  onClick={() => handleTierFilter(tier)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    tierFilter === tier
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tier === 'all' ? 'All' : tier}
                </button>
              ))}
            </div>
          </div>

          {/* Sales Play Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')} Sales Play</h3>
            <div className="flex flex-wrap gap-2">
              {['all', 'Greenfield', 'Portal Upgrade', 'E-Commerce Int', 'Rip & Replace'].map(sp => (
                <button
                  key={sp}
                  onClick={() => handleSalesPlayFilter(sp)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    salesPlayFilter === sp
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {sp === 'all' ? 'All' : sp}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.leads.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.leads.stats.total')}</dt>
                <dd className="font-medium text-gray-900">{response?.total || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pagination.page')}</dt>
                <dd className="font-medium text-gray-900">{response?.page || 1} {t('pagination.of')} {response?.totalPages || 1}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.leads.title')}</h1>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setShowForm(!showForm)}
                className="flex-1 flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
              >
                <PlusIcon className="h-5 w-5" />
                {showForm ? t('common.cancel') : t('pages.leads.addButton')}
              </button>
              <Link
                to="/leads/mailing-export"
                className="flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 ring-1 ring-gray-300"
              >
                <EnvelopeIcon className="h-5 w-5" />
              </Link>
            </div>
            {showForm && (
              <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h2 className="text-lg font-medium mb-4">{t('pages.leads.newTitle')}</h2>
                <LeadForm
                  onSubmit={(data) => createMutation.mutate(data)}
                  onCancel={() => setShowForm(false)}
                  loading={createMutation.isPending}
                />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-x-auto shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortableHeader field="name">
                          <span className="pl-4 sm:pl-6">{t('fields.name')}</span>
                        </SortableHeader>
                        <SortableHeader field="company">
                          {t('fields.organisation')}
                        </SortableHeader>
                        <SortableHeader field="status">
                          {t('fields.status')}
                        </SortableHeader>
                        <SortableHeader field="rating">
                          {t('fields.rating')}
                        </SortableHeader>
                        <SortableHeader field="tier">Tier</SortableHeader>
                        <SortableHeader field="category">{t('fields.category')}</SortableHeader>
                        <SortableHeader field="b2bScore">B2B Score</SortableHeader>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.owner')}
                        </th>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {sortedLeads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            <div className="flex items-center gap-x-3">
                              <Avatar firstName={lead.lastName ? lead.firstName : null} lastName={lead.lastName || lead.company} size="sm" />
                              <span>{lead.lastName ? `${lead.firstName || ''} ${lead.lastName}`.trim() : lead.company}</span>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500 max-w-[200px]">
                            <span className="block truncate" title={lead.company}>{lead.company}</span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <LeadStatusBadge status={lead.status} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <LeadRatingBadge rating={lead.rating} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {lead.tier && (
                              <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                lead.tier === 'Tier 1' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {lead.tier}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500 max-w-[150px]">
                            <span className="block truncate" title={lead.category || ''}>{lead.category || '-'}</span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {lead.b2bScore != null ? (
                              <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                lead.b2bScore >= 70 ? 'bg-green-100 text-green-800' :
                                lead.b2bScore >= 40 ? 'bg-amber-100 text-amber-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {lead.b2bScore}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {(() => {
                              const owner = getUser(lead.ownerId)
                              if (!owner) return '-'
                              return (
                                <div className="flex items-center gap-x-2">
                                  <Avatar firstName={owner.firstName} lastName={owner.lastName} size="xs" />
                                  <span>{owner.firstName} {owner.lastName}</span>
                                </div>
                              )
                            })()}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/leads/${lead.id}`}
                              className="inline-flex items-center gap-x-1 text-brand-600 hover:text-brand-900 mr-4"
                            >
                              <EyeIcon className="h-4 w-4" />
                              {t('common.view')}
                            </Link>
                            <button
                              onClick={() => deleteMutation.mutate(lead.id)}
                              className="inline-flex items-center gap-x-1 text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                              {t('common.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {response && (
                    <Pagination
                      page={response.page}
                      totalPages={response.totalPages}
                      total={response.total}
                      limit={response.limit}
                      onPageChange={setPage}
                      onLimitChange={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1) // Reset to first page when changing limit
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
